import {HookClass} from "@dvrt/graphql-widgets";
import {Dispatch, useEffect, useState} from "react";
import {Auth, useAuth} from "@/context/AuthProvider";
import {
    PersonalExtensionCallListLazyQueryHookResult,
    PersonalExtensionCallListQuery,
    PersonalExtensionCallListVoicemailsLazyQueryHookResult,
    PersonalExtensionCallListVoicemailsQuery,
    PersonalExtensionNumberIdLazyQueryHookResult,
    usePersonalExtensionCallListLazyQuery,
    usePersonalExtensionCallListVoicemailsLazyQuery,
    usePersonalExtensionNumberIdLazyQuery,
} from "./queries.generated";
import {useDispatch} from "react-redux";
import {showNotification} from "@/actions";
import {VOICEMAIL_FETCH_ERRORS} from "@/utils/translations";

export default class DataSource extends HookClass {
    protected extensionId: number;
    protected voicemailEnabled: boolean;

    protected callListDataQuery: PersonalExtensionCallListLazyQueryHookResult[0];
    protected voicemailsDataQuery: PersonalExtensionCallListVoicemailsLazyQueryHookResult[0];

    callListData: PersonalExtensionCallListQuery["callList"];
    private setCallListData: (v: PersonalExtensionCallListQuery["callList"]) => void;

    voicemailsData: PersonalExtensionCallListVoicemailsQuery["voicemails"];
    private setVoicemailsData: (v: PersonalExtensionCallListVoicemailsQuery["voicemails"]) => void;

    protected extensionNumberIdQuery: PersonalExtensionNumberIdLazyQueryHookResult[0];

    auth: Auth;
    dispatch: Dispatch<any>;

    useHooks(extensionId: number, voicemailEnabled: boolean) {
        this.extensionId = extensionId;
        this.voicemailEnabled = voicemailEnabled;
        this.auth = useAuth();
        this.dispatch = useDispatch();

        [this.callListData, this.setCallListData] = useState();
        [this.voicemailsData, this.setVoicemailsData] = useState();

        [this.callListDataQuery] = usePersonalExtensionCallListLazyQuery();
        [this.voicemailsDataQuery] = usePersonalExtensionCallListVoicemailsLazyQuery();
        [this.extensionNumberIdQuery] = usePersonalExtensionNumberIdLazyQuery();

        useEffect(() => {
            this.fetch();
        }, []);
    }

    async fetch() {
        const numberIdQuery = await this.extensionNumberIdQuery({
            variables: {
                extensionId: this.extensionId,
            },
            fetchPolicy: "no-cache",
        });

        const numberId = numberIdQuery.data?.users?.items[0]?.numberId;
        if (numberId === undefined) {
            this.dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.voicemail_api, "error", null, 10000));
            console.error("Failed to fetch numberId for extension", this.extensionId);
        }

        const callListResult = await this.callListDataQuery({
            variables: {
                extensionNumberId: numberId,
            },
            fetchPolicy: "no-cache",
        });

        if (this.voicemailEnabled) {
            const voicemailResult = await this.voicemailsDataQuery({
                variables: {
                    userId: this.extensionId,
                },
                fetchPolicy: "no-cache",
            });

            if (voicemailResult.data.voicemails === null) {
                this.dispatch(showNotification(VOICEMAIL_FETCH_ERRORS.voicemail_api, "error", null, 10000));
            } else if (voicemailResult.data.voicemails.errors.length > 0) {
                const translation = voicemailResult.data.voicemails.errors[0]
                    .type as keyof typeof VOICEMAIL_FETCH_ERRORS;
                const descriptor = VOICEMAIL_FETCH_ERRORS[translation] ?? VOICEMAIL_FETCH_ERRORS.voicemail_api;
                this.dispatch(showNotification(descriptor, "error", null, 10000));
            }

            this.setVoicemailsData(voicemailResult.data?.voicemails);
        }

        this.setCallListData(callListResult.data?.callList);
    }

    get loaded() {
        if (this.voicemailEnabled) {
            return !!this.callListData && !!this.voicemailsData;
        } else {
            return !!this.callListData;
        }
    }
}
